// @file Shapeshifts the grid format into a masonry layout
import { captureMessage } from '@@/bits/error_tracker'
import { $ } from '@@/bits/jquery'
import shapeshift from '@@/bits/shapeshift'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceFilterStore } from '@@/pinia/surface_filter'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfacePostSearchStore } from '@@/pinia/surface_post_search'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { debounce } from 'es-toolkit'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, onUpdated, ref, watch, type Ref } from 'vue'

// Extend JQuery interface
declare global {
  interface JQuery {
    draggable: (options?: any) => JQuery
    shapeshift: (options?: any) => JQuery
  }
}

export const useSurfaceGridShapeshift = ({
  wallContainerRef,
  options = {},
  onPostRearranged,
}: {
  wallContainerRef: Ref<HTMLElement | undefined>
  options?: { rtl?: boolean }
  onPostRearranged?: (post: HTMLElement) => void
}): void => {
  const { surfaceContainerWidth } = storeToRefs(useSurfaceContainerSizeStore())
  const { isFrozen } = storeToRefs(useSurfaceStore())
  const { areWishesManuallySorted } = storeToRefs(useSurfaceWishArrangementStore())
  const { isAPostDragging } = storeToRefs(useSurfacePostsDragAndDropStore())
  const { searchTerm } = storeToRefs(useSurfacePostSearchStore())
  const { hasSelectedFilters } = storeToRefs(useSurfaceFilterStore())

  const shouldDisableDraggingPost = computed(() => {
    if (isFrozen.value) return true
    const isPostSearchActive = searchTerm.value !== '' || hasSelectedFilters.value
    return !areWishesManuallySorted.value || isPostSearchActive
  })

  watch(shouldDisableDraggingPost, (shouldDisable) => {
    const wishListEl = $('#wish-list.wish-list-masonry')
    wishListEl.find('.surface-post.wish-editable:not(#wish-new)').each((_, el) => {
      $(el)
        .draggable()
        .draggable(shouldDisable ? 'disable' : 'enable')
    })
  })

  watch(surfaceContainerWidth, () => {
    updateOrInitGrid()
  })

  // #region GRID SHAPESHIFT
  const isInitialized = ref(false)
  const isShapeshifting = ref(false)
  let mutationObserver: MutationObserver | null = null

  const observeGridMutations = (): void => {
    if (wallContainerRef.value == null) return
    if (mutationObserver !== null) return
    mutationObserver = new MutationObserver(() => {
      if (isAPostDragging.value || isShapeshifting.value) return
      updateOrInitGrid()
    })
    mutationObserver.observe(wallContainerRef.value, { attributes: true, childList: true, subtree: true })
  }

  const initializeGrid = (): void => {
    if (wallContainerRef.value == null) return
    isInitialized.value = true
    const wallContainerEl = $(wallContainerRef.value)
    wallContainerEl.addClass('wish-not-being-dragged') // Some animation should only be applied when wishes are idle
    shapeshiftGrid()
    wallContainerEl.on('ss-rearranged', (_, item) => {
      if (item == null) {
        const posts = document.querySelectorAll('.surface-post')
        captureMessage('Grid post rearranged but post is undefined', { context: { posts, event: _ } })
        return
      }
      onPostRearranged?.(item)
    })
    wallContainerEl.on('ss-arranged', () => {
      wallContainerEl.find('.surface-post').each((index, el) => {
        $(el).attr('data-index', index + 1)
        $(el)
          .find('.wish-content')
          .attr('data-index', index + 1)
      })
    })
    wallContainerEl.on('ss-drag-start', () => {
      wallContainerEl.removeClass('wish-not-being-dragged') // Some animation should only be applied when wishes are idle
      isAPostDragging.value = true
    })
    wallContainerEl.on('ss-drop-complete', () => {
      wallContainerEl.addClass('wish-not-being-dragged') // Some animation should only be applied when wishes are idle
      isAPostDragging.value = false
    })
  }

  const shapeshiftGrid = (): void => {
    isShapeshifting.value = true
    const wishListEl = $('#wish-list.wish-list-masonry')
    wishListEl.trigger('ss-destroy')
    if (wishListEl.find('.surface-post').length !== 0) {
      const containerPaddingX = parseInt(wishListEl.css('paddingLeft'))
      const containerPaddingY = parseInt(wishListEl.css('paddingTop'))
      const gutterWidth = containerPaddingX
      wishListEl.shapeshift({
        selector: '.surface-post:not(.wish-leave)', // wishes that is leaving are excluded in grid laying-out
        dragWhitelist: '.wish-editable:not(#wish-new)', // enable dragging only for wishes that the user can edit
        enableDrag: !shouldDisableDraggingPost.value,
        enableCrossDrop: false,
        enableResize: false,
        enableTrash: false,
        gutterX: gutterWidth,
        gutterY: gutterWidth,
        paddingY: containerPaddingY,
        paddingX: containerPaddingX,
        ...options,
      })
    }
    // Flush detected mutations to observer
    setTimeout(() => (isShapeshifting.value = false), 0)
  }

  const registerDomChangeListenersForGrid = (): void => {
    if (wallContainerRef.value == null) return
    $(wallContainerRef.value).on('input.grid', 'textarea', () => {
      debouncedShapeshiftGrid()
    })
    $(wallContainerRef.value).on('beethoven-load.grid', 'img', () => {
      debouncedShapeshiftGrid()
    })
  }

  const debouncedShapeshiftGrid = debounce(shapeshiftGrid, 200)

  const updateOrInitGrid = (): void => {
    if (isInitialized.value) {
      debouncedShapeshiftGrid()
    } else {
      initializeGrid()
      registerDomChangeListenersForGrid()
    }
  }

  const destroyGrid = (): void => {
    if (wallContainerRef.value == null) return
    $(wallContainerRef.value).off('.grid').trigger('ss-destroy')
    isInitialized.value = false
    $(wallContainerRef.value).off('.grid', 'textarea,img')
  }
  // #endregion

  // #region LIFE CYCLE
  shapeshift()

  onUpdated(() => {
    observeGridMutations()
    updateOrInitGrid()
  })

  onMounted(() => {
    observeGridMutations()
  })

  onBeforeUnmount(() => {
    destroyGrid()
    mutationObserver?.disconnect()
    mutationObserver = null
  })
  // #endregion
}
